import { useNow } from '@lib/hooks/useNow';
import { getSocialConnector, platformTintStyle } from '@lib/social/scheduling/platforms';
import { describePostDetail, SocialPost } from '@lib/social/scheduling/posts';
import { POST_PRIVACY_LABELS } from '@lib/social/scheduling/types';
import { getColorFromString } from '@lib/utils/colorUtils';
import { OpenInNewRounded } from '@mui/icons-material';
import { scheduleSettings } from '@stores/ScheduleSettings';
import { socialConnectionsStore } from '@stores/SocialConnections';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { postPath } from '../../schedulePath';
import { StatusPill } from '../StatusPill/StatusPill';
import css from './PostRow.module.scss';

export const PostRow = observer(function PostRow({ post }: { post: SocialPost }) {
  const connector = getSocialConnector(post.platform);
  const now = useNow(post.status === 'processing');

  // Three states, kept apart on purpose: the list has not answered (say
  // nothing), it answered without this id (the account is gone), it has it.
  const connections = socialConnectionsStore.data;
  const account = connections?.find((c) => c.id === post.connectionId);
  const accountName =
    connections === null ? null : (account?.displayName ?? 'Disconnected account');

  const { summary } = post;
  const detail = describePostDetail(post, now);

  return (
    <div className={css.row} style={platformTintStyle(connector)} data-status={post.status}>
      <time className={css.time} dateTime={new Date(post.publishAt).toISOString()}>
        {format(scheduleSettings.inZone(post.publishAt), 'p')}
      </time>
      <span className={css.platformIcon} title={connector?.label ?? post.platform}>
        {connector?.icon}
      </span>
      <div className={css.main}>
        <div className={css.titleLine}>
          {/* The title is the way to the post's page; the row itself holds another link ("View post"), so it cannot be one. */}
          <Link to={postPath(post.id)} className={css.title}>
            {summary ? summary.headline : `Unsupported platform: ${post.platform}`}
          </Link>
        </div>
        <div className={css.meta}>
          {accountName && <span>{accountName}</span>}
          {summary?.variant && <span>{summary.variant}</span>}
          {summary?.privacy && <span>{POST_PRIVACY_LABELS[summary.privacy]}</span>}
          {detail && <span className={css.detail}>{detail}</span>}
        </div>
      </div>
      {post.permalink && (
        <a
          className={css.viewLink}
          href={post.permalink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View post"
        >
          View post
          <OpenInNewRounded />
        </a>
      )}
      <StatusPill status={post.status} />
      {post.groupId && (
        <span
          className={css.groupDot}
          style={{ background: getColorFromString(post.groupId) }}
          title="Scheduled together"
        />
      )}
    </div>
  );
});
